import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import EventRow from "@/components/EventRow";
import { axiosInstance } from "@/App";
import { Calendar } from "lucide-react";
import { VISIBLE_EVENT_CATEGORIES, filterVisibleEvents } from "@/config/eventCategories";

// All and Live Now are status views, not event categories. Their results are
// limited to the category experiences enabled in this build.
const CATEGORIES = ["All", "Live Now", ...VISIBLE_EVENT_CATEGORIES];

const Browse = ({ user, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axiosInstance.get("/events");
        setEvents(filterVisibleEvents(response.data));
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const visibleEvents = activeCategory === "All"
    ? events
    : activeCategory === "Live Now"
      ? events.filter((event) => event.status === "live")
      : events.filter((event) => (event.category || "").toLowerCase() === activeCategory.toLowerCase());

  return (
    <div className="h-dvh overflow-y-auto hide-scrollbar bg-[#0f0f0f]" data-testid="browse-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="pt-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20">
        <h1 className="text-white text-4xl md:text-6xl font-black mb-8">Browse Events</h1>

        {/* Category chips */}
        <div className="hide-scrollbar flex gap-3 mb-10 overflow-x-auto overscroll-x-contain scroll-smooth pb-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`shrink-0 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${
                activeCategory === cat
                  ? "bg-blue-600 border-blue-500 text-white"
                  : "bg-gray-900/60 border-white/10 text-gray-300 hover:bg-gray-800"
              }`}
              data-testid={`browse-category-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-gray-400 text-lg py-20 text-center">Loading events…</div>
        ) : visibleEvents.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-14 h-14 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {activeCategory === "Live Now" ? "No live events right now." : `No events in ${activeCategory} right now.`}
            </p>
          </div>
        ) : (
          <div data-testid="browse-grid">
            <EventRow
              title={activeCategory === "All" ? "All Events" : activeCategory}
              events={visibleEvents}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Browse;
